import { RootStore } from "./../root-store/root-store"
import { api } from "./../../services/api"
import { Instance, SnapshotOut, types, flow, getParent, cast } from "mobx-state-tree"
import { TokenValidResponse, FlowType } from "eteva-rest-api"
import { IUser, RoleType, Roles, PageResponse, LoginResponse, AuthError } from "eteva-types"
import { omit } from "ramda"
import { withEnvironment } from "../extensions"

const LoginStates = ["LOGGED_IN" as const, "LOGGED_OUT" as const, "IN_PROGRESS" as const]

/**
 *  A model for the status of the current user
 */
const UserModel = types.model("UsersModel", {
  id: types.identifierNumber,
  email: types.string,
  role: types.enumeration(["SUPERADMIN", "ADMIN", "EDITOR"]),
})

export type UserModelType = Instance<typeof UserModel>

export const UserStoreModel = types
  .model("UserStore")
  .props({
    state: types.enumeration("State", LoginStates),
    user: types.maybe(UserModel),
    currentUser: types.maybe(UserModel),
    token: types.maybe(types.string),
    loginError: types.maybe(types.string),
    users: types.optional(types.array(UserModel), []),
    usersTotal: types.optional(types.number, 0),
    fetchingMe: types.optional(types.boolean, false),
    fetching: types.optional(types.boolean, false),
  })
  .extend(withEnvironment)
  .views(self => ({
    get isLoggedIn() {
      return self.state === "LOGGED_IN"
    },
    get isAdmin() {
      return self.currentUser?.role === Roles.ADMIN
    },
    get isSuperAdmin() {
      return self.currentUser?.role === Roles.SUPERADMIN
    },
    get isAtLeastAdmin() {
      return self.currentUser?.role === Roles.ADMIN || self.currentUser?.role === Roles.SUPERADMIN
    },
  }))
  .actions(self => ({
    getCurrentUser: flow(function* (): FlowType {
      self.fetchingMe = true
      const result = yield api.users.getMe()
      self.fetchingMe = false
      if (result) {
        self.currentUser = cast(result)
        return true
      }
      return false
    }),
  }))
  .actions(self => ({
    login: flow(function* (email: string, password: string): FlowType {
      const { messageStore } = getParent(self) as RootStore
      const { api } = self.environment

      self.state = "IN_PROGRESS"
      self.fetching = true
      const result = yield api.login(email, password)
      if (result) {
        self.state = "LOGGED_IN"
      } else {
        messageStore.setMessage("message.error.login", "error")
        self.state = "LOGGED_OUT"
      }
      self.fetching = false
    }),
    logout() {
      api.cleanUserData()
      self.state = "LOGGED_OUT"
      self.currentUser = undefined
    },
    checkToken: flow(function* (): FlowType {
      const tokenValid: TokenValidResponse = yield api.checkIfTokenIsValid()
      if (tokenValid === TokenValidResponse.valid) {
        self.state = "LOGGED_IN"
      }
      if (tokenValid === TokenValidResponse.invalid) {
        self.state = "LOGGED_OUT"
      }
    }),
    requestResetPasswordEmail: flow(function* (email: string) {
      const { messageStore } = getParent(self) as RootStore
      self.state = "IN_PROGRESS"
      const result = yield api.auth.resetPassword(email, true)
      self.state = "LOGGED_OUT"
      if (result.ok) {
        messageStore.setMessage("message.success.user.linkSendSuccess", "success")
        return true
      }
      messageStore.setMessage("message.error.common", "error")
      return false
    }),
    setPassword: flow(function* (code: string, email: string, password: string): FlowType {
      const { messageStore } = getParent(self) as RootStore
      self.state = "IN_PROGRESS"
      const result = yield api.auth.setPassword(code, email, password)
      self.state = "LOGGED_OUT"
      if (result.ok) {
        return true
      }
      messageStore.setMessage("message.error.common", "error")
      return false
    }),
    addUser: flow(function* (user: IUser): FlowType {
      self.fetching = true
      const { messageStore } = getParent(self) as RootStore
      const result: boolean | LoginResponse = yield api.users.create(user)
      if (result && typeof result === "boolean") {
        messageStore.setMessage("message.success.user.added", "success")
        self.fetching = false
        return true
      }
      if (typeof result === "object" && result.error === AuthError.EmailInUse) {
        messageStore.setMessage("message.error.user.emailInUse", "error")
      } else {
        messageStore.setMessage("message.error.common", "error")
      }
      self.fetching = false
      return false
    }),
    getUser: flow(function* (userId): FlowType {
      self.fetching = true
      const { messageStore } = getParent(self) as RootStore
      const result = yield api.users.getOne(userId)
      if (result) {
        self.fetching = false
        return result
      }
      messageStore.setMessage("message.error.common", "error")
      self.fetching = false
      return null
    }),
    updateUser: flow(function* (user: IUser) {
      self.fetching = true
      const { messageStore } = getParent(self) as RootStore
      const result = yield api.users.update(user)
      if (result) {
        messageStore.setMessage("message.success.user.updated", "success")
        self.fetching = false
        return true
      }
      messageStore.setMessage("message.error.common", "error")
      self.fetching = false
      return false
    }),
    deleteUser: flow(function* (userId): FlowType {
      const { messageStore } = getParent(self) as RootStore
      const result = yield api.users.delete(userId)
      if (result) {
        messageStore.setMessage("message.success.user.deleted", "success")
        return true
      }
      messageStore.setMessage("message.error.common", "error")
      return false
    }),
    getUsers: flow(function* (page: number, pageSize: number, search?: string, role?: RoleType): FlowType {
      self.fetching = true
      const { messageStore } = getParent(self) as RootStore
      const response: PageResponse<IUser> = yield api.users.getAll({ page, pageSize, search }, role)
      if (response) {
        self.users = cast(response.results)
        self.usersTotal = response.total
        self.fetching = false
        return true
      }
      messageStore.setMessage("message.error.common", "error")
      self.fetching = false
      return false
    }),
    deleteBatch: flow(function* (itemIds: number[]): FlowType {
      const { messageStore } = getParent(self) as RootStore
      if (!self.currentUser?.id) return false
      if (itemIds.indexOf(self.currentUser?.id) >= 0) {
        messageStore.setMessage("message.error.user.cannotDeleteSelf", "error")
        return false
      }
      self.fetching = true
      const response: Promise<boolean> = yield api.users.deleteBatch(itemIds)
      self.fetching = false
      if (!response) messageStore.setMessage("message.error.common", "error")
      return response
    }),
  }))
  .postProcessSnapshot(omit(["fetching"]))
/**
  * Un-comment the following to omit model attributes from your snapshots (and from async storage).
  * Useful for sensitive data like passwords, or transitive state like whether a modal is open.

  * Note that you'll need to import `omit` from ramda, which is already included in the project!
  */

type UserStoreType = Instance<typeof UserStoreModel>
export interface UserStore extends UserStoreType {}
type UserStoreSnapshotType = SnapshotOut<typeof UserStoreModel>
export interface UserStoreSnapshot extends UserStoreSnapshotType {}
